import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { login } from "../../services/authService";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      const currentLang = lang || i18n.language || "en";
      navigate(`/${currentLang}/transcription`); // Redirect after successful login
    } catch (err) {
      setError(t("login.errors.failed"));
    }
  };

  return (
    <div className="flex items-start justify-center min-h-screen px-4">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold text-center mb-6">
          {t("login.title")}
        </h2>
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              {t("login.labels.email")}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
              aria-label={t("login.labels.email")}
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              {t("login.labels.password")}
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
              aria-label={t("login.labels.password")}
            />
            <div className="mt-2 text-right">
              <Link
                to={`/${lang || i18n.language || "en"}/password-reset`}
                className="text-sm text-blue-600 hover:underline"
              >
                {t("login.links.forgotPassword")}
              </Link>
            </div>
          </div>
          {error && <Alert variant="danger">{error}</Alert>}
          <Button type="submit" variant="primary" className="w-full">
            {t("login.actions.login")}
          </Button>
        </form>
        <p className="text-xs mt-4">
          {t("login.agreement.text")}
          <a
            href="https://www.interview-ai.site/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600"
          >
            {t("login.agreement.terms")}
          </a>
          {t("login.agreement.and")}
          <a
            href="https://www.interview-ai.site/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600"
          >
            {t("login.agreement.privacyPolicy")}
          </a>
          {t("login.agreement.agreement")}
        </p>
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">{t("login.signup.prompt")}</p>
          <Link to={`/${lang || i18n.language || "en"}/register`}>
            <Button variant="outline" className="mt-2 w-full">
              {t("login.signup.action")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
